import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ChevronDown, Check } from "lucide-react";
import profileIcon from "../../../assets/profile_icon.svg";
import CalendarPicker from "screens/Project Dashboard/Tasks/CalenderPicker";
import { ProjectApi } from "api/projects/service";
import { WorkOrderApi } from "api/workorders/service";

const AddWorkOrder = ({ contractor, onClose }) => {
  const toLocalISODate = (dateLike) => {
    const d = new Date(dateLike);
    const tzOffsetMs = d.getTimezoneOffset() * 60000;
    const local = new Date(d.getTime() - tzOffsetMs);
    return local.toISOString().slice(0, 10);
  };
  const today = toLocalISODate(new Date());
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [showPopup, setShowPopup] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [contractAmount, setContractAmount] = useState("");
  const [scopeOfWork, setScopeOfWork] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [termsAndConditions, setTermsAndConditions] = useState("");
  const [approvalAndAcknowledgement, setApprovalAndAcknowledgement] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const projectDropdownRef = useRef(null);
  const submittingRef = useRef(false);

  const calculateDuration = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.max(end - start, 0);
    return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
    if (!orgId) return;
    (async () => {
      try {
        const list = await ProjectApi.listSitesByOrganization(orgId);
        const normalized = Array.isArray(list?.data)
          ? list.data
          : Array.isArray(list?.results)
          ? list.results
          : Array.isArray(list)
          ? list
          : [];
        setProjects(normalized || []);
      } catch (e) {
        console.error("Failed to load projects", e);
        setProjects([]);
      }
    })();
  }, []);

  // Close project dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        projectDropdownRef.current &&
        !projectDropdownRef.current.contains(event.target)
      ) {
        setProjectDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const submitWorkOrder = async () => {
    if (submittingRef.current) return; // Prevent multiple rapid clicks
    if (!contractor?.id) {
      alert("Missing contractor");
      return;
    }
    if (!selectedProject?.id) {
      alert("Please select a project");
      return;
    }
    if (!contractAmount) {
      alert("Please enter contract amount");
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await WorkOrderApi.create({
        contractor: Number(contractor.id),
        site: Number(selectedProject.id),
        end_date: toLocalISODate(endDate),
        contract_amount: Number(contractAmount),
        scope_of_work: scopeOfWork,
        payment_terms: paymentTerms,
        terms_and_conditions: termsAndConditions,
        approval_and_acknowledgement: approvalAndAcknowledgement,
      });
      // setShowPopup(true);
      onClose();
    } catch (e) {
      console.error("Failed to create work order", e);
      alert(e?.message || "Failed to create work order");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-30 z-50 flex justify-end"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-[48%] min-w-[720px] h-full overflow-y-scroll flex flex-col p-2 relative bg-[#F7F7F8]"
        onClick={(e) => e.stopPropagation()}
      >
      <div className="flex mb-4">
        <button onClick={onClose} className="text-[#232323] hover:text-black">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <p className="ml-[30px] text-[#232323] text-[20px]">Work Order</p>
      </div>

      <div className="bg-white border shadow-md rounded-xl p-4 shrink-0">
        <div className="flex items-center gap-3 mb-4">
          <img src={profileIcon} alt="Contractor" className="w-10 h-10" />
          <div>
            <p className="font-medium text-[16px] text-[#232323]">
              {contractor?.name || "Contractor"}{" "}
              <span className="text-xs bg-[#E3E3E3] text-[#737373] px-2 py-1 rounded-full ml-2">
                Contractor
              </span>
            </p>
            <p className="text-[14px] font-light text-[#232323]">{contractor?.contractor_type || ""}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-3">
          <div>
            <label className="text-[15px] text-[#232323] font-md block mb-1">
              Date of Issue
            </label>
            <input
              type="date"
              className="bg-[#F3F4F6] h-[48px] p-2 rounded-lg w-full text-sm text-[#232323]"
              defaultValue={today}
            />
          </div>
          <div>
            <label className="text-[15px] text-[#232323] font-md block mb-1">
              Select Project
            </label>
            <div className="relative" ref={projectDropdownRef}>
              <div
                className="flex bg-[#F3F4F6] h-[40px] items-center justify-between p-2 rounded-lg cursor-pointer"
                onClick={() => setProjectDropdownOpen((v) => !v)}
              >
                <span className="text-[15px] font-medium text-[#737373]">
                  {selectedProject ? selectedProject.site_name : "Project unassigned"}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </div>
              {projectDropdownOpen && (
                <div className="absolute z-20 mt-1 w-full max-h-48 overflow-auto bg-white border rounded-md shadow">
                  {projects.length === 0 && (
                    <div className="px-3 py-2 text-sm text-[#737373]">No projects</div>
                  )}
                  {projects.map((p) => (
                    <div
                      key={p.id}
                      className="px-3 py-2 hover:bg-[#F3F4F6] cursor-pointer text-[14px] text-[#232323]"
                      onClick={() => {
                        setSelectedProject({ id: p.id, site_name: p.site_name });
                        setProjectDropdownOpen(false);
                      }}
                    >
                      {p.site_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <label className="text-[15px] text-[#232323] font-md block mb-1">Contract Amount</label>
          <input
            type="number"
            min="0"
            placeholder="Enter amount"
            className="bg-[#F3F4F6] h-[48px] p-2 rounded-lg w-full text-sm text-[#232323]"
            value={contractAmount}
            onChange={(e) => setContractAmount(e.target.value)}
          />
        </div>
      </div>

        <div className="grid grid-cols-2 gap-4 items-start mb-2">
          <div>
            <label className="text-[15px] text-[#232323] font-md block mb-1">
              Select Start & End Date
            </label>
            <div className="bg-[#F3F4F6] h-[48px] w-full rounded-lg px-3 py-2 flex items-center justify-between text-[#232323] cursor-pointer">
              <CalendarPicker
                startDate={startDate}
                endDate={endDate}
                onDateChange={handleDateChange}
              />
              <span className="text-sm">
                {startDate && endDate
                  ? `${formatDate(startDate)} - ${formatDate(endDate)}`
                  : "Select dates"}
              </span>
            </div>
          </div>
          <div className="text-[16px] font-medium text-[#3B0069] bg-[#ECE6F0] px-3 py-1 rounded-full h-[30px] w-fit mt-10">
            Contract Duration: {calculateDuration()} Days
          </div>
        </div>
      </div>

      <div className="mt-2 pr-1">
        <div className="bg-white mt-2 shadow-md rounded-xl p-4 space-y-5">
          <div>
            <label className="text-[15px] font-md text-[#232323] block mb-1">
              Scope of Work
            </label>
            <textarea
              placeholder="Type"
              className="w-full bg-[#F3F4F6] p-3 rounded-lg text-[15px] font-medium text-[#737373] resize-none h-[75px]"
              value={scopeOfWork}
              onChange={(e) => setScopeOfWork(e.target.value)}
            />
          </div>
          <div>
            <label className="text-[15px] font-md text-[#232323] block mb-1">
              Payment terms
            </label>
            <textarea
              placeholder="Type"
              className="w-full bg-[#F3F4F6] p-3 rounded-lg text-[15px] font-medium text-[#737373] resize-none h-[75px]"
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
            />
          </div>
          <div>
            <label className="text-[15px] font-md text-[#232323] block mb-1">
              Terms & Conditions
            </label>
            <textarea
              placeholder="Type"
              className="w-full bg-[#F3F4F6] p-3 rounded-lg text-[15px] font-medium text-[#737373] resize-none h-[75px]"
              value={termsAndConditions}
              onChange={(e) => setTermsAndConditions(e.target.value)}
            />
          </div>
          <div>
            <label className="text-[15px] font-md text-[#232323] block mb-1">
              Approval & Acknowledgement
            </label>
            <textarea
              placeholder="Type"
              className="w-full bg-[#F3F4F6] p-3 rounded-lg text-[15px] font-medium text-[#737373] resize-none h-[75px]"
              value={approvalAndAcknowledgement}
              onChange={(e) => setApprovalAndAcknowledgement(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="mt-1 mb-1 flex justify-center">
        <button
          onClick={submitWorkOrder}
          disabled={submitting}
          className="bg-[#3B0069] disabled:opacity-70 disabled:cursor-not-allowed text-white px-10 py-2 rounded-[12px] font-medium hover:bg-purple-800 flex items-center gap-2"
        >
          Generate
          <div className="w-6 h-6 rounded-full bg-[#5E2D91] flex items-center justify-center">
            <Check className="w-4 h-4 text-white" />
          </div>
        </button>
      </div>

      {showPopup && (
        <div className="fixed inset-0 bg-[black] bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-[300px] text-center space-y-4">
            <h2 className="text-[18px] font-semibold text-[#232323]">
              Work Order Generated
            </h2>
            <p className="text-sm text-[#555]">What would you like to do next?</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => {
                  setShowPopup(false);
                  // Add your View Summary navigation logic
                }}
                className="bg-[#3B0069] text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-800"
              >
                View Summary
              </button>
              <button
                onClick={() => {
                  setShowPopup(false);
                  // Add your download logic
                }}
                className="border border-[#3B0069] text-[#3B0069] px-4 py-2 rounded-lg font-medium hover:bg-[#ECE6F0]"
              >
                Download PDF
              </button>
            </div>
            <button
              onClick={() => setShowPopup(false)}
              className="text-sm text-gray-500 mt-2 hover:text-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AddWorkOrder;
